import React, { useState, useEffect, useRef } from 'react';
import {
  FaCheckCircle,
  FaUserGraduate,
  FaUsers,
  FaLaptopCode,
  FaShieldAlt,
  FaAward,
  FaChartLine,
  FaRocket,
  FaLightbulb,
  FaHeartbeat,
  FaChevronDown,
  FaChevronUp,
  FaQuestionCircle,
  FaBriefcase,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaSpinner
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config/api';

const About = () => {
  const [counts, setCounts] = useState({
    years: 0,
    formations: 0,
    students: 0,
    satisfaction: 0
  });

  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);

  // Refs pour les sections à animer
  const sectionsRef = useRef([]);

  useEffect(() => {
    
    sectionsRef.current.forEach((section) => {
      if (section) {
        section.classList.add('animate-in');
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);

    const targets = {
      years: 15,
      formations: 500,
      students: 10000,
      satisfaction: 95
    };

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const increment = {
      years: targets.years / steps,
      formations: targets.formations / steps,
      students: targets.students / steps,
      satisfaction: targets.satisfaction / steps
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      if (currentStep < steps) {
        setCounts({
          years: Math.min(Math.floor(increment.years * currentStep), targets.years),
          formations: Math.min(Math.floor(increment.formations * currentStep), targets.formations),
          students: Math.min(Math.floor(increment.students * currentStep), targets.students),
          satisfaction: Math.min(Math.floor(increment.satisfaction * currentStep), targets.satisfaction)
        });
        currentStep++;
      } else {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Gestion de la soumission de la newsletter
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingNewsletter(true);
    setNewsletterError(null);
    setNewsletterSuccess(false);

    try {
      const response = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail,
          source: 'page_about'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmittingNewsletter(false);
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        
       
        setTimeout(() => {
          setNewsletterSuccess(false);
        }, 5000);
      } else {
        // Gestion des erreurs de validation
        if (data.errors && data.errors.email) {
          throw new Error(data.errors.email[0]);
        } else if (data.message) {
          throw new Error(data.message);
        } else {
          throw new Error('Erreur lors de l\'inscription');
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setNewsletterError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setIsSubmittingNewsletter(false);
    }
  };

  const features = [
    { icon: FaCheckCircle, text: 'Paiement en ligne sécurisé' },
    { icon: FaCheckCircle, text: 'Support technique 24/7' },
    { icon: FaCheckCircle, text: 'Transactions rapides' },
    { icon: FaCheckCircle, text: 'Application Web responsive' },
    { icon: FaCheckCircle, text: 'Paiements groupés' },
    { icon: FaCheckCircle, text: 'Protection des données' }
  ];

  const advantages = [
    {
      icon: FaShieldAlt,
      title: 'Partenaire de Confiance',
      description: 'Des formations de haute qualité dispensées par des experts certifiés et reconnus dans leur domaine.',
      color: '#fd7e14'
    },
    {
      icon: FaRocket,
      title: 'Plateforme Rapide',
      description: 'Accédez instantanément à nos formations et ressources pédagogiques via notre plateforme moderne.',
      color: '#198754'
    },
    {
      icon: FaHeartbeat,
      title: 'Fiabilité Testée',
      description: 'Plus de 15 ans d\'expérience avec un taux de satisfaction de 95% auprès de nos stagiaires.',
      color: '#dc3545'
    }
  ];

  const stats = [
    { value: counts.years, label: 'Années d\'Expérience', suffix: '+', icon: FaAward },
    { value: counts.formations, label: 'Formations', suffix: '+', icon: FaChartLine },
    { value: counts.students, label: 'Stagiaires Formés', suffix: '+', icon: FaUsers },
    { value: counts.satisfaction, label: 'Satisfaction', suffix: '%', icon: FaUserGraduate }
  ];

  const poles = [
    {
      icon: FaBriefcase,
      title: 'Pôle GRH',
      description: 'Gestion des richesses humaines des entités - Recrutement, détection et gestion des talents.',
      color: '#fd7e14',
      link: 'https://www.cinef-bj.com/recrutement/'
    },
    {
      icon: FaGraduationCap,
      title: 'Pôle Formation',
      description: 'Présentiel, Blended learning et E-learning - Plus de 100 formations professionnelles.',
      color: '#198754',
      link: 'https://www.cinef-bj.com/formations/'
    },
    {
      icon: FaLightbulb,
      title: 'Pôle Conseil et Expertise',
      description: 'Étude, Assistance et Accompagnement des entités dans leur développement.',
      color: '#8b5cf6',
      link: 'https://cinef-expertises.com/'
    },
    {
      icon: FaLaptopCode,
      title: 'Pôle IT',
      description: 'Transformation digitale, solutions et intégration web pour votre entreprise.',
      color: '#20c997',
      link: 'https://cinef.app/'
    }
  ];

  const faqData = [
    {
      question: 'Quels sont les domaines de formation proposés par CINEF ?',
      answer: 'CINEF propose plus de 100 formations professionnelles dans 11 domaines d\'expertise à travers 7 pays (Cotonou, Lomé, Niger, Abidjan, Libreville, Casablanca et Paris). Nos formations couvrent la gestion, le management, les ressources humaines, la finance, le marketing et bien d\'autres domaines.'
    },
    {
      question: 'Les certifications CINEF sont-elles reconnues internationalement ?',
      answer: 'Oui, absolument. CINEF est partenaire de PECB et délivre des certifications reconnues internationalement. Nos formations sont dispensées par des formateurs qualifiés et chevronnés, garantissant une reconnaissance professionnelle dans les pays de l\'espace OHADA et au-delà.'
    },
    {
      question: 'Quelles sont les modalités de formation disponibles ?',
      answer: 'Nous proposons trois modalités : formation en présentiel dans nos différents centres, blended learning (mixte présentiel et à distance) et e-learning via notre plateforme. Nous offrons également un suivi accompagnement après chaque session pour vous aider dans la mise en œuvre des acquis.'
    },
    {
      question: 'Comment puis-je vérifier l\'authenticité de mon attestation de formation ?',
      answer: 'CINEF met à disposition un système de vérification en ligne des attestations sur cinef-certificate.com. Vous pouvez y vérifier l\'authenticité de votre certificat à tout moment de manière sécurisée.'
    },
    {
      question: 'Quel est le processus d\'inscription aux formations ?',
      answer: 'Le processus est simple : consultez notre catalogue de formations, choisissez la formation qui vous convient, contactez-nous via notre site ou par téléphone (+229 63 18 60 60), et notre équipe vous accompagnera dans toutes les démarches d\'inscription.'
    },
    {
      question: 'CINEF propose-t-il des formations sur mesure pour les entreprises ?',
      answer: 'Oui, nous élaborons des formations sur mesure adaptées aux besoins particuliers de chaque entreprise. Notre équipe pédagogique en perpétuelle veille sur les mutations des métiers vous propose des solutions personnalisées pour maximiser votre retour sur investissement.'
    }
  ];

  return (
    <>
      <Header />
      {/* CONTENU JSX */}
      <div className="about-page-wrapper">
        {/* Hero Section - Nouveau Design */}
        <section className="about-hero">
          <div className="about-container">
            <div className="hero-badge">
              <FaAward className="badge-icon" />
              <span>Ressentez la Commodité</span>
            </div>

            <h1 className="hero-title">
              Formez-vous, progressez, <br />
              <span className="hero-gradient">développez vos compétences facilement</span>
            </h1>

            <p className="hero-description">
              CINEF vous offre un accès simple et rapide à des formations professionnelles de qualité.
              Développez vos compétences et boostez votre carrière avec nos programmes certifiants
              reconnus internationalement.
            </p>

            <div className="hero-buttons">
              <a href="/ledevis" className="btn-hero btn-primary">
                Commencer Maintenant
              </a>

              <a href="/contact" className="btn-hero btn-secondary">
                En Savoir Plus
              </a>
            </div>
          </div>
        </section>

        {/* Newsletter Section avec Image */}
        <section className="newsletter-section scroll-animate" ref={el => sectionsRef.current[0] = el}>
          <div className="about-container">
            <div className="newsletter-grid">
              {/* Left - Purple Card */}
              <div className="newsletter-card">
                <h3 className="newsletter-title">Abonnez-vous à notre Newsletter</h3>
                <p className="newsletter-text">
                  Découvrez chaque jour de nouvelles formations et restez à jour avec les dernières
                  tendances du marché professionnel.
                </p>
                
                {/* Message de succès */}
                {newsletterSuccess && (
                  <div className="newsletter-success-message">
                    <FaCheckCircle />
                    <span>Inscription réussie ! Merci de votre confiance.</span>
                  </div>
                )}

                {/* Message d'erreur */}
                {newsletterError && (
                  <div className="newsletter-error-message">
                    {newsletterError}
                  </div>
                )}

                <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                  <input
                    type="email"
                    placeholder="Votre Email"
                    className="newsletter-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    disabled={isSubmittingNewsletter}
                  />
                  <button 
                    type="submit" 
                    className="newsletter-button"
                    disabled={isSubmittingNewsletter}
                  >
                    {isSubmittingNewsletter ? (
                      <>
                        <FaSpinner className="spinner" />
                        <span>Envoi...</span>
                      </>
                    ) : (
                      'Demander'
                    )}
                  </button>
                </form>
              </div>

              {/* Right - Features List */}
              <div className="features-card">
                <h3 className="features-title">Comment Puis-Je Vous Aider ?</h3>
                <p className="features-text">
                  CINEF vous accompagne à chaque étape de votre parcours de formation avec des
                  services adaptés à vos besoins.
                </p>
                <div className="features-list">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="feature-item">
                        <Icon className="feature-icon" />
                        <span>{feature.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Company Section avec Images */}
        <section className="about-company scroll-animate" ref={el => sectionsRef.current[1] = el}>
          <div className="about-container">
            <div className="company-content">
              {/* Left - Images */}
              <div className="company-images">
                <div className="main-image-wrapper">
                  <img
                    src="../assets/images/MDEDJI.jpeg"
                    alt="Professionnel confiant"
                    className="main-image"
                  />
                </div>
                <div className="secondary-image-wrapper">
                  <img
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&q=80"
                    alt="Professionnelle souriante"
                    className="secondary-image"
                  />
                </div>
                <div className="stats-badge">
                  <div className="stats-number">1,485</div>
                  <div className="stats-plus">+</div>
                  <div className="stats-label">Clients de Confiance</div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="company-text">
                <div className="section-badge">
                  <span className="badge-dot"></span>
                  <span>À Propos de Notre Entreprise</span>
                </div>

                <h2 className="section-title">
                  Nous sommes le meilleur <br />
                  organisme de formation en ligne et en présentiel
                </h2>

                <p className="section-description">
                  CINEF est un centre de formation professionnelle reconnu dans plusieurs pays africains et
                  Européens. Nous proposons des formations certifiantes dans plusieurs domaines d'expertise.
                </p>

                <div className="advantages-grid">
                  {advantages.map((advantage, index) => {
                    const Icon = advantage.icon;
                    return (
                      <div
                        key={index}
                        className="advantage-item"
                        style={{ '--advantage-color': advantage.color }}
                      >
                        <div className="advantage-icon-wrapper">
                          <Icon />
                        </div>
                        <div className="advantage-text">
                          <h4>{advantage.title}</h4>
                          <p>{advantage.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="statistics-section scroll-animate" ref={el => sectionsRef.current[2] = el}>
          <div className="about-container">
            <div className="stats-grid">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="stat-card">
                    <div className="stat-number">
                      {stat.value}
                      <span className="stat-suffix">{stat.suffix}</span>
                    </div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Nos 4 Pôles Section */}
        <section className="poles-section scroll-animate" ref={el => sectionsRef.current[3] = el}>
          <div className="about-container">
            <div className="poles-header">
              <div className="section-badge centered">
                <span className="badge-dot"></span>
                <span>Nos pôles d'Expertise</span>
              </div>
              <h2 className="section-title centered">
                4 Pôles pour vous accompagner <br />
                dans votre développement
              </h2>
              <p className="section-description centered">
                CINEF structure ses services autour de 4 pôles complémentaires pour répondre
                à tous vos besoins en développement des compétences et gestion des talents.
              </p>
            </div>

            <div className="poles-grid">
              {poles.map((pole, index) => {
                const Icon = pole.icon;
                return (
                  <div
                    key={index}
                    className="pole-card"
                    style={{ '--pole-color': pole.color }}
                  >
                    <div className="pole-icon">
                      <Icon />
                    </div>

                    <h3 className="pole-title">{pole.title}</h3>
                    <p className="pole-description">{pole.description}</p>

                    <a href={pole.link} className="pole-link" target="_blank" rel="noopener noreferrer">
                      Découvrir
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Présence Internationale Section */}
        <section className="presence-section scroll-animate" ref={el => sectionsRef.current[4] = el}>
          <div className="about-container">
            <div className="presence-content">
              <div className="presence-left">
                <div className="section-badge">
                  <span className="badge-dot"></span>
                  <span>Présence Internationale</span>
                </div>
                <h2 className="section-title">
                  Présents dans 7 pays <br />
                  pour mieux vous servir
                </h2>
                <p className="section-description">
                  CINEF est implanté dans les pays de l'espace OHADA et en France,
                  vous offrant un accès privilégié à nos formations professionnelles
                  partout où vous êtes.
                </p>
              </div>
              <div className="presence-right">
                <div className="locations-grid">
                  {['Cotonou', 'Lomé', 'Niger', 'Abidjan', 'Libreville', 'Casablanca', 'Paris'].map((city, index) => (
                    <div key={index} className="location-item">
                      <FaMapMarkerAlt className="location-icon" />
                      <span>{city}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section scroll-animate" ref={el => sectionsRef.current[5] = el}>
          <div className="about-container">
            <div className="faq-header">
              <div className="section-badge centered">
                <FaQuestionCircle className="badge-icon-small" />
                <span>Questions Fréquentes</span>
              </div>
              <h2 className="section-title centered">
                Des Questions ? <br />
                Nous avons les réponses
              </h2>
              <p className="section-description centered">
                Retrouvez les réponses aux questions les plus fréquemment posées sur nos formations
                et nos services.
              </p>
            </div>

            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <div
                    className={`faq-question ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  >
                    <h3>{faq.question}</h3>
                    {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  <div className={`faq-answer ${activeIndex === index ? 'active' : ''}`}>
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* STYLES CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .about-page-wrapper {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow-x: hidden;
        }

        .about-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* CORRECTION: Animations de scroll - Toutes les sections visibles par défaut */
        .scroll-animate {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
                      transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .scroll-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Hero Section - Nouveau Design Centré */
        .about-hero {
          padding: 100px 0 80px;
          text-align: center;
          position: relative;
          overflow: hidden;
          margin-top: 30px;
        }

        .about-hero::before {
          content: '';
          position: absolute;
          top: -20%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(253, 126, 20, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          animation: floatBubble 20s ease-in-out infinite;
        }

        .about-hero::after {
          content: '';
          position: absolute;
          bottom: -20%;
          left: -10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(25, 135, 84, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          animation: floatBubble 15s ease-in-out infinite reverse;
        }

        @keyframes floatBubble {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: white;
          padding: 10px 24px;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
          font-size: 14px;
          font-weight: 600;
          color: #fd7e14;
          position: relative;
          z-index: 1;
          animation: fadeInDown 0.8s ease-out;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .badge-icon {
          animation: rotate 3s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .hero-title {
          font-size: 56px;
          font-weight: 900;
          line-height: 1.15;
          color: #1a202c;
          margin-bottom: 20px;
          letter-spacing: -1.5px;
          position: relative;
          z-index: 1;
          animation: fadeInUp 0.8s ease-out 0.2s backwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-gradient {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 50%, #198754 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 5s ease infinite;
          background-size: 200% 200%;
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .hero-description {
          font-size: 18px;
          line-height: 1.7;
          color: #4a5568;
          margin: 0 auto 32px;
          max-width: 700px;
          position: relative;
          z-index: 1;
          animation: fadeInUp 0.8s ease-out 0.4s backwards;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
          animation: fadeInUp 0.8s ease-out 0.6s backwards;
        }

        .btn-hero {
          padding: 16px 36px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          border: none;
          font-family: inherit;
          position: relative;
          overflow: hidden;
        }

        .btn-hero::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .btn-hero:hover::before {
          width: 300px;
          height: 300px;
        }

        .btn-primary {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          box-shadow: 0 8px 24px rgba(253, 126, 20, 0.35);
        }

        .btn-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 36px rgba(253, 126, 20, 0.45);
        }

        .btn-secondary {
          background: white;
          color: #fd7e14;
          border: 2px solid #fd7e14;
        }

        .btn-secondary:hover {
          background: #fd7e14;
          color: white;
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(253, 126, 20, 0.3);
        }

        /* Newsletter Section */
        .newsletter-section {
          padding: 80px 0;
          background: white;
        }

        .newsletter-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
        }

        .newsletter-card {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          border-radius: 24px;
          padding: 48px;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .newsletter-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -30%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 4s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        .newsletter-title {
          font-size: 28px;
          font-weight: 800;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }

        .newsletter-text {
          font-size: 15px;
          opacity: 0.95;
          line-height: 1.6;
          margin-bottom: 28px;
          position: relative;
          z-index: 1;
        }

        /* Messages de succès et d'erreur pour la newsletter */
        .newsletter-success-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(25, 135, 84, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 14px;
          font-weight: 600;
          position: relative;
          z-index: 1;
          animation: slideInDown 0.4s ease-out;
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .newsletter-error-message {
          padding: 12px 16px;
          background: rgba(220, 53, 69, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 14px;
          font-weight: 600;
          position: relative;
          z-index: 1;
          animation: slideInDown 0.4s ease-out;
        }

        .newsletter-form {
          display: flex;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .newsletter-input {
          flex: 1;
          padding: 14px 20px;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-family: inherit;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          color: white;
          transition: all 0.3s ease;
        }

        .newsletter-input::placeholder {
          color: rgba(255, 255, 255, 0.7);
        }

        .newsletter-input:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.25);
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
        }

        .newsletter-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .newsletter-button {
          padding: 14px 28px;
          background: white;
          color: #8b5cf6;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .newsletter-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3);
        }

        .newsletter-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .features-card {
          background: white;
          border-radius: 24px;
          padding: 48px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .features-title {
          font-size: 24px;
          font-weight: 800;
          color: #1a202c;
          margin-bottom: 12px;
        }

        .features-text {
          font-size: 15px;
          color: #718096;
          line-height: 1.6;
          margin-bottom: 28px;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 28px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 15px;
          color: #4a5568;
          font-weight: 500;
        }

        .feature-icon {
          font-size: 18px;
          color: #198754;
          animation: bounceIn 0.6s ease-out;
        }

        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }

        /* About Company Section */
        .about-company {
          padding: 100px 0;
          background: #fafafa;
        }

        .company-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }

        .company-images {
          position: relative;
        }

        .main-image-wrapper {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .main-image {
          width: 100%;
          display: block;
          transition: transform 0.6s ease;
        }

        .main-image-wrapper:hover .main-image {
          transform: scale(1.05);
        }

        .secondary-image-wrapper {
          position: absolute;
          bottom: -40px;
          left: -40px;
          width: 280px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
          border: 6px solid white;
        }

        .secondary-image {
          width: 100%;
          display: block;
          transition: transform 0.6s ease;
        }

        .secondary-image-wrapper:hover .secondary-image {
          transform: scale(1.1);
        }

        .stats-badge {
          position: absolute;
          bottom: 40px;
          right: -40px;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          color: white;
          padding: 32px 28px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 16px 48px rgba(139, 92, 246, 0.4);
        }

        .stats-number {
          font-size: 48px;
          font-weight: 900;
          line-height: 1;
          margin-bottom: 4px;
        }

        .stats-plus {
          font-size: 36px;
          font-weight: 900;
          color: #20c997;
        }

        .stats-label {
          font-size: 14px;
          opacity: 0.95;
          font-weight: 600;
          margin-top: 4px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #fd7e14;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
        }

        .section-badge.centered {
          margin: 0 auto 20px;
        }

        .section-badge.light {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          color: white;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #fd7e14;
          border-radius: 50%;
          animation: ping 2s ease-in-out infinite;
        }

        @keyframes ping {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.7; }
        }

        .section-title {
          font-size: 42px;
          font-weight: 900;
          color: #1a202c;
          margin-bottom: 20px;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .section-title.centered {
          text-align: center;
        }

        .section-description {
          font-size: 16px;
          color: #718096;
          line-height: 1.8;
          margin-bottom: 32px;
        }

        .section-description.centered {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 32px;
        }

        .advantages-grid {
          display: grid;
          gap: 20px;
        }

        .advantage-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 24px;
          background: white;
          border-radius: 16px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid transparent;
        }

        .advantage-item:hover {
          transform: translateX(10px);
          border-color: var(--advantage-color);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
        }

        .advantage-icon-wrapper {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: var(--advantage-color);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .advantage-item:hover .advantage-icon-wrapper {
          transform: scale(1.1) rotate(-5deg);
        }

        .advantage-text h4 {
          font-size: 18px;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 8px;
        }

        .advantage-text p {
          font-size: 14px;
          color: #718096;
          line-height: 1.7;
        }

        /* Statistics Section */
        .statistics-section {
          padding: 80px 0;
          background: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 32px;
        }

        .stat-card {
          text-align: center;
          padding: 48px 32px;
          background: #fafafa;
          border-radius: 20px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-12px);
          background: white;
          border-color: #fd7e14;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .stat-number {
          font-size: 56px;
          font-weight: 900;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
          margin-bottom: 12px;
          animation: countUp 1s ease-out;
        }

        @keyframes countUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .stat-suffix {
          font-size: 48px;
        }

        .stat-label {
          font-size: 15px;
          color: #718096;
          font-weight: 600;
        }

        /* Nos 4 Pôles Section */
        .poles-section {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
        }

        .poles-header {
          margin-bottom: 60px;
        }

        .poles-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
        }

        .pole-card {
          background: white;
          padding: 48px 36px;
          border-radius: 24px;
          border: 2px solid #e2e8f0;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .pole-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 0;
          height: 4px;
          background: var(--pole-color);
          transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pole-card:hover::before {
          width: 100%;
        }

        .pole-card:hover {
          transform: translateY(-12px);
          border-color: var(--pole-color);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
        }

        .pole-icon {
          width: 80px;
          height: 80px;
          margin-bottom: 24px;
          background: var(--pole-color);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 36px;
          transition: all 0.4s ease;
        }

        .pole-card:hover .pole-icon {
          transform: scale(1.1) rotate(-5deg);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
        }

        .pole-title {
          font-size: 22px;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 12px;
        }

        .pole-description {
          font-size: 15px;
          color: #718096;
          line-height: 1.7;
          margin-bottom: 20px;
        }

        .pole-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: transparent;
          color: var(--pole-color);
          border: 2px solid var(--pole-color);
          border-radius: 10px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          text-decoration: none;
        }

        .pole-link:hover {
          background: var(--pole-color);
          color: white;
          transform: translateX(5px);
        }

        /* Présence Internationale Section */
        .presence-section {
          padding: 100px 0;
          background: white;
        }

        .presence-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }

        .locations-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .location-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 24px;
          background: #f8f9fa;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          color: #1a202c;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .location-item:hover {
          background: white;
          border-color: #fd7e14;
          transform: translateX(8px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .location-icon {
          color: #fd7e14;
          font-size: 20px;
        }

        /* FAQ Section */
        .faq-section {
          padding: 100px 0;
        }

        .faq-header {
          margin-bottom: 60px;
        }

        .badge-icon-small {
          font-size: 14px;
        }

        .faq-container {
          max-width: 900px;
          margin: 0 auto;
        }

        .faq-item {
          background: white;
          border-radius: 16px;
          margin-bottom: 16px;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .faq-item:hover {
          border-color: #fd7e14;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .faq-question {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 28px;
          cursor: pointer;
          user-select: none;
          transition: all 0.3s ease;
        }

        .faq-question:hover {
          background: #fafafa;
        }

        .faq-question h3 {
          font-size: 17px;
          font-weight: 600;
          color: #1a202c;
          margin: 0;
          flex: 1;
          padding-right: 20px;
        }

        .faq-question svg {
          color: #fd7e14;
          font-size: 20px;
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .faq-question.active {
          background: #fafafa;
        }

        .faq-question.active svg {
          transform: rotate(180deg);
        }

        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                      padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .faq-answer.active {
          max-height: 500px;
          padding: 0 28px 24px;
        }

        .faq-answer p {
          font-size: 15px;
          color: #718096;
          line-height: 1.8;
          margin: 0;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .hero-title {
            font-size: 42px;
          }

          .newsletter-grid,
          .company-content,
          .presence-content {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .poles-grid {
            grid-template-columns: 1fr;
          }

          .locations-grid {
            grid-template-columns: 1fr;
          }

          .secondary-image-wrapper,
          .stats-badge {
            display: none;
          }
        }

        @media (max-width: 640px) {
          .about-hero {
            padding: 80px 0 60px;
          }

          .about-container {
            padding: 0 16px;
          }

          .hero-title {
            font-size: 32px;
          }

          .hero-description {
            font-size: 16px;
          }

          .hero-buttons {
            flex-direction: column;
            width: 100%;
          }

          .btn-hero {
            width: 100%;
            justify-content: center;
          }

          .newsletter-card,
          .features-card {
            padding: 32px 24px;
          }

          .newsletter-form {
            flex-direction: column;
          }

          .newsletter-button {
            width: 100%;
          }

          .stats-grid,
          .poles-grid,
          .locations-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .section-title {
            font-size: 28px;
          }

          .faq-question h3 {
            font-size: 15px;
          }

          .faq-answer p {
            font-size: 14px;
          }

          .pole-card {
            padding: 32px 24px;
          }

          .location-item {
            font-size: 14px;
            padding: 16px 20px;
          }
        }
      `}</style>
      <Footer />
    </>
  );
};

export default About;